import { Component } from 'react';
import { ConnectedProps, connect } from 'react-redux';
import { History } from 'history';
import { fixedEncodeURIComponent, getLetterGradeFromNumberGrade } from './../GlobalFunctions';
import { getCompletionStatus } from './../core/context/contextHelpers';
import { countCompletionStatuses } from './../completionStatus';
import { showIntercomNewMessage } from '../IntercomUtilityFunctions';
import { selectClassroom } from '../Classrooms';
import { RootState } from '../ApplicationState';
import { SessionState } from '../Session';
import { ClassroomType } from '@peekapak/types/DataProtocolTypes';
import PrimaryButton from '../components/PrimaryButton/PrimaryButton';
import peekavilleLogo from '../images/logo-myPeekaville.png';
import styles from './../SCSS/MyPeekavilleBanner.module.scss';

type Props = PropsFromRedux & {
  history: History;
  session: SessionState;
  classroom: ClassroomType;
  unit: string;
};

class MyPeekavilleBanner extends Component<Props> {
  constructor(props: Props) {
    super(props);

    this.state = {};

    this.getCompletionStatus = this.getCompletionStatus.bind(this);
    this.redirectToGuide = this.redirectToGuide.bind(this);
  }

  redirectToGuide() {
    const { classroom } = this.props;
    const classroomName = classroom.className;
    const grade = getLetterGradeFromNumberGrade(classroom.grade);

    const unit = 'Getting-Started';

    this.props.history.push(`/${fixedEncodeURIComponent(classroomName)}/lessonPlan/${unit}-${grade}`);
  }

  async getCompletionStatus(props: Props) {
    if (props.session !== undefined) {
      const classroom = this.props.classrooms.list[this.props.classrooms.selectedClassroom];
      const classroomId = classroom.classroomId;
      const grade = getLetterGradeFromNumberGrade(classroom.grade);
      const unitId = `${props.unit}-${grade}`;

      try {
        const completionStatus = await getCompletionStatus(classroomId, unitId);
        const myStatuses = countCompletionStatuses(completionStatus);

        const percentage = Math.round((myStatuses.Completed / myStatuses.Total) * 100);
        this.setState({ percentComplete: percentage });

        if (myStatuses.InProgress > 0 || myStatuses.Completed > 0) {
          this.setState({ isStarted: true });
        } else {
          this.setState({ isStarted: false });
        }

        if (percentage === 100) {
          this.setState({
            isStarted: false,
            isCompleted: true,
          });
        }
      } catch (error) {
        throw error;
      }
    }
  }

  componentDidMount() {
    this._isMounted = true;
    this.getCompletionStatus(this.props);
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    this.getCompletionStatus(nextProps);
  }

  requestUpgrade = () => {
    showIntercomNewMessage(`I'm interested in learning more about upgrading to Peekapak PRO!`);
  };

  render() {
    return (
      <div className={styles.myPeekavilleIntro}>
        <div className={styles.explanatoryElements}>
          <div>
            <h2 className={styles.myPeekavilleHeader}>Introducing myPeekaville</h2>
            <p>
              myPeekaville is Peekapak’s student-led learning game that helps students reflect on the lessons taught in
              class, while providing teachers with progress and comprehension data used to better support student
              learning.
            </p>
          </div>
          <div className={styles.buttonContainer}>
            <PrimaryButton className={styles.myPeekavilleBannerButton} small onClick={() => this.redirectToGuide()}>
              Guide to <br /> getting Started&nbsp;
              <i className='fa fa-arrow-right' />
            </PrimaryButton>
            <PrimaryButton
              className={styles.myPeekavilleBannerButton}
              small
              onClick={() => window.open('https://www.mypeekaville.com/login', '_blank')}
            >
              Login to <br /> myPeekaville&nbsp;
              <i className='fa fa-arrow-right' />
            </PrimaryButton>
          </div>
        </div>
        <div className={styles.logo}>
          <img src={peekavilleLogo} alt='myPeekaville Logo' className={styles.myPeekavilleImage} />
        </div>
      </div>
    );
  }
}

const mapStateToProps = (state: RootState) => ({
  classroom: selectClassroom(state),
  classrooms: {
    state: state.classrooms.state,
    list: state.classrooms.classrooms,
    selectedClassroom: state.classrooms.selectedClassroom,
  },
});

const connector = connect(mapStateToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

export default connect(mapStateToProps, null)(MyPeekavilleBanner);
